/* ==================================================
  c_embroidery-sku-select.js
  カスタム刺繍オーダーの SKU 選択モーダル
  Pulse（刺繍アプリ）のボタン押下を横取りしてモーダルを開き、
  選択した SKU をフォームへ反映してから Pulse のボタンへクリックを渡す
  カラーは画像付きドロップダウン（listbox）、サイズはネイティブ select
===================================================*/

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-embroidery-sku-select]';
  const TRIGGER_SELECTOR = '.c_action-buttons__primary';
  /*  Pulse が action_buttons 内に複製して差し込むボタン  */
  const PULSE_BUTTON_SELECTOR = '.personalize-button';
  const BODY_OPEN_CLASS = 'c_embroidery-sku-select-open';
  /*  Pulse の SKU 登録確認（products/exists）の完了を待つ上限  */
  const READY_TIMEOUT = 10000;
  const READY_INTERVAL = 100;

  /* ==================================================
    Pulse 連携
  ===================================================*/

  function isVisible(el) {
    return !!el && window.getComputedStyle(el).display !== 'none';
  }

  /*  Pulse はページ内で最初の form [name=id] を読むため、全フォームの値を揃える  */
  function getVariantInputs() {
    return Array.from(document.querySelectorAll('form [name="id"]'));
  }

  /*  Pulse はバリアント変更時にボタンを一旦隠し、SKU 登録確認後に表示し直す。
      隠れた後に再表示されたら、新しい SKU の遷移先が設定済み  */
  function waitForPulseReady(trigger) {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      let hasHidden = false;
      const timer = window.setInterval(() => {
        const visible = isVisible(trigger.querySelector(PULSE_BUTTON_SELECTOR));
        if (!visible) hasHidden = true;
        if (visible && hasHidden) {
          window.clearInterval(timer);
          resolve();
          return;
        }
        if (Date.now() - startedAt > READY_TIMEOUT) {
          window.clearInterval(timer);
          reject(new Error('Pulse のボタンが再表示されませんでした'));
        }
      }, READY_INTERVAL);
    });
  }

  function applyVariant(trigger, variantId) {
    const inputs = getVariantInputs();
    if (!inputs.length) return Promise.reject(new Error('form [name=id] が見つかりません'));

    /*  同じ SKU で change を発火すると、Pulse はボタンを隠したまま再表示しないため何もしない  */
    if (inputs[0].value === variantId && isVisible(trigger.querySelector(PULSE_BUTTON_SELECTOR))) {
      return Promise.resolve();
    }

    inputs.forEach((input) => {
      input.value = variantId;
    });
    /*  Pulse は全 form の change を監視しているため、発火は 1 回でよい  */
    const form = inputs[0].form;
    if (form) form.dispatchEvent(new Event('change', { bubbles: true }));

    return waitForPulseReady(trigger);
  }

  /* ==================================================
    モーダル
  ===================================================*/

  function parseVariants(modal) {
    try {
      const json = modal.querySelector('[data-embroidery-sku-select-variants]');
      return json ? JSON.parse(json.textContent) : [];
    } catch (error) {
      console.warn('[c_embroidery-sku-select] SKU データを読み込めませんでした', error);
      return [];
    }
  }

  function init() {
    /*  テーマエディタでの再描画時に、body へ移した旧モーダルが残らないようにする  */
    document.querySelectorAll(`${ROOT_SELECTOR}[data-initialized]`).forEach((el) => el.remove());

    const modal = document.querySelector(ROOT_SELECTOR);
    const trigger = document.querySelector(TRIGGER_SELECTOR);
    if (!modal || !trigger) return;

    /*  選べる SKU が 1 つ以下なら選択は不要なので、Pulse の通常動作に任せる  */
    const variants = parseVariants(modal).map((v) => ({ ...v, id: String(v.id) }));
    if (variants.filter((v) => v.available).length <= 1) {
      modal.remove();
      return;
    }

    modal.setAttribute('data-initialized', '');
    /*  祖先の transform / overflow の影響を受けないよう body 直下へ移す  */
    document.body.appendChild(modal);

    const dropdown = modal.querySelector('[data-embroidery-sku-select-dropdown]');
    const colorButton = modal.querySelector('[data-embroidery-sku-select-color-button]');
    const colorButtonImage = modal.querySelector('[data-embroidery-sku-select-color-button-image]');
    const colorButtonName = modal.querySelector('[data-embroidery-sku-select-color-button-name]');
    const colorList = modal.querySelector('[data-embroidery-sku-select-color-list]');
    const colorOptions = Array.from(colorList.querySelectorAll('[role="option"]'));
    const sizeSelect = modal.querySelector('[data-embroidery-sku-select-size]');
    const submitButton = modal.querySelector('[data-embroidery-sku-select-submit]');
    const errorEl = modal.querySelector('[data-embroidery-sku-select-error]');
    const noImage = modal.dataset.noImage;
    const soldOutFormat = modal.dataset.soldOutFormat || '__LABEL__';
    let selectedColor = '';
    let activeOption = null;
    let isPassthrough = false;
    let isBusy = false;

    function getColorVariants(color) {
      return variants.filter((v) => v.color === color);
    }

    function isOptionDisabled(option) {
      return option.getAttribute('aria-disabled') === 'true';
    }

    /*  選択中の SKU（サイズなし商品はカラーの在庫あり SKU）  */
    function getSelectedVariant() {
      if (sizeSelect) return variants.find((v) => v.id === sizeSelect.value) || null;
      return getColorVariants(selectedColor).find((v) => v.available) || null;
    }

    /* ------------------------------
      カラー（画像付きドロップダウン）
    ------------------------------*/

    function setActiveOption(option) {
      if (activeOption) activeOption.classList.remove('is-active');
      activeOption = option;
      if (!option) {
        colorList.removeAttribute('aria-activedescendant');
        return;
      }
      option.classList.add('is-active');
      colorList.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    function openList() {
      if (isBusy) return;
      colorList.hidden = false;
      colorButton.setAttribute('aria-expanded', 'true');
      const selected = colorOptions.find((option) => option.dataset.value === selectedColor);
      setActiveOption(selected || colorOptions.find((option) => !isOptionDisabled(option)));
      colorList.focus();
    }

    function closeList(returnFocus) {
      if (colorList.hidden) return;
      colorList.hidden = true;
      colorButton.setAttribute('aria-expanded', 'false');
      setActiveOption(null);
      if (returnFocus) colorButton.focus();
    }

    function selectColor(color) {
      selectedColor = color;
      colorOptions.forEach((option) => {
        option.setAttribute('aria-selected', option.dataset.value === color ? 'true' : 'false');
      });
      const selected = colorOptions.find((option) => option.dataset.value === color);
      const image = selected ? selected.dataset.image : '';
      colorButtonImage.setAttribute('src', image || noImage);
      colorButtonImage.classList.toggle('is-no-image', !image);
      colorButtonName.textContent = color;
    }

    /*  上下キーで選択可能な候補間を移動（売り切れはスキップ）  */
    function moveActive(step) {
      const enabled = colorOptions.filter((option) => !isOptionDisabled(option));
      if (!enabled.length) return;
      const index = enabled.indexOf(activeOption);
      const next = index === -1 ? enabled[0] : enabled[Math.min(Math.max(index + step, 0), enabled.length - 1)];
      setActiveOption(next);
    }

    function chooseOption(option) {
      if (!option || isOptionDisabled(option)) return;
      selectColor(option.dataset.value);
      renderSizes(sizeSelect ? sizeSelect.value : null);
      refresh();
      closeList(true);
    }

    /* ------------------------------
      サイズ（ネイティブ select）
    ------------------------------*/

    /*  選択中カラーのサイズで選択肢を作り直す。可能なら指定 SKU、なければ最初の在庫ありを選ぶ  */
    function renderSizes(preferredId) {
      if (!sizeSelect) return;
      const colorVariants = getColorVariants(selectedColor);
      sizeSelect.innerHTML = '';
      colorVariants.forEach((v) => {
        const option = document.createElement('option');
        option.value = v.id;
        option.textContent = v.available ? v.size : soldOutFormat.replace('__LABEL__', v.size);
        option.disabled = !v.available;
        sizeSelect.appendChild(option);
      });
      const preferred = colorVariants.find((v) => v.id === preferredId && v.available);
      const target = preferred || colorVariants.find((v) => v.available);
      if (target) sizeSelect.value = target.id;
    }

    /* ------------------------------
      状態更新
    ------------------------------*/

    function updateSubmit() {
      const selected = getSelectedVariant();
      submitButton.disabled = isBusy || !selected || !selected.available;
    }

    function refresh() {
      errorEl.hidden = true;
      updateSubmit();
    }

    function setBusy(busy) {
      isBusy = busy;
      colorButton.disabled = busy;
      if (sizeSelect) sizeSelect.disabled = busy;
      submitButton.classList.toggle('is-loading', busy);
      submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      updateSubmit();
    }

    function openModal() {
      /*  現在フォームに入っている SKU を初期選択にする（在庫切れなら最初の在庫ありカラー）  */
      const currentInput = getVariantInputs()[0];
      const current = currentInput ? variants.find((v) => v.id === currentInput.value && v.available) : null;
      const initial = current || variants.find((v) => v.available);
      selectColor(initial.color);
      renderSizes(initial.id);

      closeList(false);
      setBusy(false);
      refresh();
      modal.hidden = false;
      document.body.classList.add(BODY_OPEN_CLASS);
      colorButton.focus();
    }

    function closeModal() {
      if (isBusy) return;
      closeList(false);
      modal.hidden = true;
      document.body.classList.remove(BODY_OPEN_CLASS);
    }

    /* ==================================================
      イベント
    ===================================================*/

    /*  キャプチャで受けて、Pulse のボタンのクリック処理より先に止める  */
    trigger.addEventListener('click', (e) => {
      if (isPassthrough) return;
      if (!e.target.closest(PULSE_BUTTON_SELECTOR)) return;
      e.preventDefault();
      e.stopPropagation();
      openModal();
    }, true);

    colorButton.addEventListener('click', () => {
      if (colorList.hidden) {
        openList();
      } else {
        closeList(true);
      }
    });

    colorButton.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openList();
      }
    });

    colorList.addEventListener('click', (e) => {
      chooseOption(e.target.closest('[role="option"]'));
    });

    colorList.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveActive(-1);
          break;
        case 'Home':
          e.preventDefault();
          setActiveOption(colorOptions.find((option) => !isOptionDisabled(option)));
          break;
        case 'End':
          e.preventDefault();
          setActiveOption(colorOptions.filter((option) => !isOptionDisabled(option)).pop());
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          chooseOption(activeOption);
          break;
        case 'Escape':
          /*  モーダルごと閉じないよう、一覧だけ閉じて止める  */
          e.preventDefault();
          e.stopPropagation();
          closeList(true);
          break;
        case 'Tab':
          closeList(false);
          break;
        default:
          break;
      }
    });

    /*  一覧の外をクリックしたら一覧を閉じる  */
    modal.addEventListener('click', (e) => {
      if (!colorList.hidden && !dropdown.contains(e.target)) closeList(false);
      if (e.target.closest('[data-embroidery-sku-select-close]')) closeModal();
    });

    if (sizeSelect) sizeSelect.addEventListener('change', refresh);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    submitButton.addEventListener('click', async () => {
      const selected = getSelectedVariant();
      if (!selected || !selected.available || isBusy) return;

      setBusy(true);
      try {
        await applyVariant(trigger, selected.id);
        const pulseButton = trigger.querySelector(PULSE_BUTTON_SELECTOR);
        if (!pulseButton) throw new Error('Pulse のボタンが見つかりません');
        isPassthrough = true;
        pulseButton.click();
      } catch (error) {
        console.warn('[c_embroidery-sku-select] 刺繍画面へ遷移できませんでした', error);
        setBusy(false);
        errorEl.hidden = false;
      } finally {
        isPassthrough = false;
      }
    });

    /*  刺繍画面からブラウザバックで戻った際（bfcache）に処理中の状態を解除する  */
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      setBusy(false);
      closeModal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
